/**
 * Показ счётчика объёма ленты.
 *
 * Чистый модуль без React по правилу lib/*.ts: vitest в этом проекте включает
 * только `*.test.ts`, компонентных тестов нет, поэтому всё, что решает, ЧТО за
 * число видит оператор, живёт здесь.
 *
 * # Что здесь решается
 *
 * Оператор насчитал по ленте порядка пяти тысяч там, где интерфейс Polymarket
 * показывал «$16.5K Vol.». Разбор дал две причины разом, и обе видны только когда
 * числа стоят рядом:
 *
 *   - ЕДИНИЦЫ РАЗНЫЕ. «$… Vol.» у биржи — это Σ size, то есть ШТУКИ КОНТРАКТОВ,
 *     несмотря на знак доллара: на закрытых окнах `gamma.volume` совпадал с Σ size
 *     по всем сделкам Data API до восьмого знака, а Σ (size × price) был меньше в
 *     полтора-два раза. Сравнивать наш нотионал с их «Vol.» бессмысленно;
 *   - ЧУЖОЕ ЧИСЛО ОТСТАЁТ. У живого пятиминутного окна Gamma пересчитывает объём
 *     минутами, и свежий наш итог против старого чужого выглядит расхождением,
 *     которым не является.
 *
 * Поэтому здесь нет функции «во сколько раз мы ошиблись»: она давала бы уверенный
 * ответ на вопрос, который без возраста чужого числа не имеет смысла.
 */

import type { TapeStats } from "../api/types";
import { plural } from "./format";
import { format, groupThousands, parse, roundToFixed, type Micro } from "./money";

/**
 * Число share для строки счётчика: «1 234.5», «62 191».
 *
 * Знаков ноль от тысячи и выше, один — ниже: объём в десятки тысяч читают порядком
 * величины, а на тихом окне в пару сотен share десятая доля ещё различима. Дробные
 * хвосты здесь не нужны никому — это не цена и не размер ордера.
 */
export function formatShares(value: string | null | undefined): string {
  const m = parse(value);
  if (m === null) return "—";
  return groupThousands(roundToFixed(m, Math.abs(m) >= 1_000 * 1_000_000 ? 0 : 1));
}

/** Деньги для строки счётчика: «$987», «$1 760.6». */
export function formatDollars(value: string | null | undefined): string {
  const m = parse(value);
  if (m === null) return "—";
  return `$${groupThousands(roundToFixed(m, Math.abs(m) >= 1_000 * 1_000_000 ? 0 : 1))}`;
}

/**
 * Строка счётчика: кто считал и что насчитал.
 *
 * Раньше каждая величина склеивалась в одну длинную строку («сделок 285 ·
 * 3 675.6 share · $1 760.6»), и в узкой колонке рынка она рвалась посреди
 * числа — «$642.» на одной строке, «1» на другой. Разбивка на поля нужна не
 * ради красоты: числа сравнивают ГЛАЗОМ по вертикали (наши 759.9 share против
 * их 280.3), а сравнивать можно только выровненные колонки.
 */
export interface TapeRow {
  /** Кто считал. Он же ключ строки. */
  label: string;
  /** Сделок. Пусто у числа биржи: она сообщает объём, но не число сделок. */
  trades?: string;
  shares: string;
  /**
   * Деньги. У числа биржи ОТСУТСТВУЮТ, и это главный инвариант строки: «$… Vol.»
   * в её интерфейсе — это Σ size, то есть штуки контрактов. Поставить сюда
   * доллар значило бы предложить оператору сравнить нотионал с количеством.
   */
  money?: string;
  /**
   * Приписка справа: возраст чужого числа и момент начала нашего счёта.
   *
   * ВИДИМАЯ, а не в подсказке. У живого пятиминутного окна число биржи отстаёт
   * минутами, и без возраста разница читается как потеря сделок — то есть даёт
   * ответ, обратный правде. Метка начала счёта нужна по родственной причине:
   * трейдер подписывается на рынок за минуты до окна, а сделки идут и раньше.
   */
  note?: string;
}

/**
 * Что показывать в счётчике ленты, сверху вниз.
 *
 * Пустой массив — счёта нет вовсе; вызывающий обязан показать вместо него
 * причину словами. Ноль читался бы как «сделок не было», а это другой факт.
 */
export function tapeRows(t: TapeStats | undefined, nowMs: number): TapeRow[] {
  const rows: TapeRow[] = [];
  if (t?.known) {
    const ours: TapeRow = {
      label: "наша лента",
      trades: String(t.trades),
      shares: formatShares(t.shares),
      money: formatDollars(t.notional),
    };
    const since = sinceLabel(t);
    if (since) ours.note = since;
    rows.push(ours);

    // Наши сделки — ОТДЕЛЬНОЙ строкой от публичных принтов: они едут в ту же
    // ленту из приватного канала, и сложенные вместе посчитались бы дважды.
    if (t.our_trades) {
      const mine: TapeRow = {
        label: "наши сделки",
        trades: String(t.our_trades),
        shares: formatShares(t.our_shares),
      };
      if (t.our_notional) mine.money = formatDollars(t.our_notional);
      rows.push(mine);
    }
  }

  const exchange = parse(t?.exchange);
  if (t && exchange !== null && exchange > 0) {
    const row: TapeRow = { label: "Polymarket", shares: formatShares(t.exchange) };
    const age = exchangeAge(t, nowMs);
    if (Number.isFinite(age)) row.note = formatAgeShort(age);
    rows.push(row);
  }
  return rows;
}

/** Возраст числа Polymarket в миллисекундах. NaN, если метки нет. */
export function exchangeAge(t: TapeStats | undefined, nowMs: number): number {
  if (!t?.exchange_at) return NaN;
  const at = new Date(t.exchange_at).getTime();
  return Number.isNaN(at) ? NaN : nowMs - at;
}

/**
 * С какого момента идёт наш счёт: «счёт с 13:05:00».
 *
 * Показывается ВСЕГДА, когда метка есть. Трейдер подписывается на рынок за минуты
 * до окна, а сделки по нему идут и раньше (замер: первая за 16 минут до открытия),
 * поэтому неполнота нашего итога — это нормальное состояние, а не поломка, и
 * читать его надо вместе с этой меткой.
 */
export function sinceLabel(t: TapeStats | undefined): string {
  if (!t?.known || !t.since) return "";
  const at = new Date(t.since);
  if (Number.isNaN(at.getTime())) return "";
  return `счёт с ${at.toLocaleTimeString("ru-RU", { hour12: false })}`;
}

/**
 * Похоже ли на двойной счёт по двум токенам.
 *
 * Отвечает НЕ «двойной счёт есть», а «две половины разбивки почти равны», и разница
 * принципиальна: по кадрам `last_trade_price` нельзя решить, две это сделки или
 * одна, показанная с двух сторон, — идентификатора сделки у события нет. Поэтому
 * функция только подсказывает, куда смотреть, а решает сверка (`make gate-tape`).
 *
 * Порог 5 % и не меньше десяти сделок: на паре принтов «почти равны» случайно.
 */
export function looksDoubleCounted(t: TapeStats | undefined): boolean {
  if (!t?.known || !t.tokens || t.tokens.length !== 2) return false;
  if (t.trades < 10) return false;
  const [a, b] = t.tokens;
  if (!a || !b) return false;
  const x = parse(a.shares);
  const y = parse(b.shares);
  if (x === null || y === null || x <= 0 || y <= 0) return false;
  return Math.abs(x - y) * 20 <= Math.max(x, y);
}

/**
 * Отсечка мелочи в ленте: порог из поля ввода. null — фильтра нет.
 *
 * Пустое поле, ноль и мусор дают ОДНО И ТО ЖЕ — «показывать всё». Ноль потому,
 * что «от 0» ничего не отсекает; мусор потому, что поле читается на каждое
 * нажатие, и половина набранного числа («1.», «−») не должна на мгновение
 * прятать ленту, которую как раз смотрят.
 */
export function sizeThreshold(raw: string): Micro | null {
  const v = parse(raw);
  return v === null || v <= 0 ? null : v;
}

/**
 * Сделки не мельче порога — в SHARE, как в колонке «Размер».
 *
 * В share, а не в долларах: отсекают мелочь глазом по той самой колонке, и
 * фильтр по другой величине означал бы, что строка на 200 share исчезает, а
 * соседняя на 150 остаётся — по цене, которой в условии не было.
 *
 * Строки с НЕРАЗБИРАЕМЫМ размером остаются видимыми: спрятать их значило бы
 * выдать «размер неизвестен» за «сделка мельче порога», а это разные факты.
 */
export function bySize<T extends { size: string }>(
  trades: readonly T[],
  min: Micro | null,
): readonly T[] {
  if (min === null) return trades;
  return trades.filter((t) => {
    const size = parse(t.size);
    return size === null || size >= min;
  });
}

/**
 * Подпись отсечки. Скрытое называется вслух и числом: молча укоротившаяся лента
 * читается как затишье на рынке, а это ответ, обратный правде.
 */
export function hiddenBySizeNote(hidden: number, min: Micro | null): string {
  if (min === null || hidden <= 0) return "";
  const verb = plural(hidden, "скрыта", "скрыты", "скрыто");
  const noun = plural(hidden, "сделка", "сделки", "сделок");
  return `отсечка от ${format(min)} share: ${verb} ${hidden} ${noun}`;
}

/** Возраст коротко: «12 с», «2 мин», «1 ч». */
function formatAgeShort(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s} с`;
  if (s < 3600) return `${Math.round(s / 60)} мин`;
  return `${Math.round(s / 3600)} ч`;
}

